import type { CustomField } from '@puckeditor/core';

import { NumberField } from './widgets/number-field';

/** JSON schema for a `NumberField('px')`-backed value, e.g. '8px'. */
export const PX_AI_SCHEMA = { type: 'string', pattern: '^\\d+px$' } as const;

const SPACING_SIDE_LABELS = { top: 'Top', right: 'Right', bottom: 'Bottom', left: 'Left' } as const;

type SpacingSide = keyof typeof SPACING_SIDE_LABELS;

/**
 * Builds the four-sided `top`/`right`/`bottom`/`left` custom fields shared by every CMS
 * component with a `padding` prop (Text/internal `Paragraph`, Heading, Button).
 *
 * Centralised rather than duplicated per-component because a `custom` field missing
 * `ai.schema` doesn't just make that one field un-editable by AI — Puck's AI plugin 400s
 * on the *entire* request the moment it reaches this field, breaking the whole chat for
 * that component. Hand-copying these four fields into each component risked exactly that
 * (see the `paragraph`/`heading`/`button` git history before this helper existed).
 */
export function createSpacingFields({ max }: { max?: number } = {}): Record<SpacingSide, CustomField<string>> {
  const fields = {} as Record<SpacingSide, CustomField<string>>;

  (Object.keys(SPACING_SIDE_LABELS) as SpacingSide[]).forEach((side) => {
    const label = SPACING_SIDE_LABELS[side];
    fields[side] = {
      type: 'custom',
      label,
      render: NumberField('px', undefined, max),
      ai: {
        schema: (PX_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions: `${label} padding, e.g. 8px.${max === undefined ? '' : ` Use 0 to ${max}px.`}`,
      },
    };
  });

  return fields;
}
